<template>
    <div class="ds-dossier-body">
        <div class="ds-dossier-subhead">
            <button type="button" class="ds-dossier-back" :disabled="pending" @click="emit('back')">
                <BackIcon />{{ backLabel }}
            </button>
        </div>
        <span ref="userSection" class="ds-dossier-subtitle">Личные данные</span>

        <!-- public_api_fields User: замок только у полей массива, остальные — по правам. -->
        <div v-if="canEditUser && locksKnown && oneCLockedCount" class="ds-dossier-info">
            <LockIcon />
            <span>Поля с замком приходят из 1С и меняются там.</span>
        </div>
        <div v-if="canEditUser && !locksKnown" class="ds-dossier-info is-error" role="alert">
            <LockIcon />
            <span>Не удалось проверить поля из 1С — изменение личных данных недоступно. <button type="button" class="ds-dossier-link" @click="reloadUser()">Обновить</button></span>
        </div>
        <div v-if="!canEditUser" class="ds-dossier-info">
            <LockIcon />
            <span>Изменение личных данных недоступно с текущими правами.</span>
        </div>

        <div class="ds-dossier-grid">
            <div v-for="field in nameFields" :key="field.key" class="ds-dossier-field">
                <span class="ds-dossier-field-label">{{ field.label }}</span>
                <span v-if="fieldLocked(field.key)" class="ds-dossier-locked" :title="lockTitle(field.key)">
                    <span>{{ draft[field.key] || DASH }}</span><LockIcon />
                </span>
                <base-input v-else v-model="draft[field.key]" :label="false" max-length="20" :disabled="pending" />
                <span v-if="errorOf(field.key)" class="ds-dossier-field-error">{{ errorOf(field.key) }}</span>
            </div>
            <div class="ds-dossier-field">
                <template v-if="fieldLocked('phone')">
                    <span class="ds-dossier-field-label">Телефон</span>
                    <span class="ds-dossier-locked" :title="lockTitle('phone')"><span>{{ draft.phone || DASH }}</span><LockIcon /></span>
                </template>
                <phone-input v-else v-model="draft.phone">Телефон</phone-input>
                <span v-if="errorOf('phone')" class="ds-dossier-field-error">{{ errorOf('phone') }}</span>
            </div>
            <div class="ds-dossier-field">
                <span class="ds-dossier-field-label">E-mail</span>
                <span v-if="fieldLocked('email')" class="ds-dossier-locked" :title="lockTitle('email')"><span>{{ draft.email || DASH }}</span><LockIcon /></span>
                <base-input v-else v-model="draft.email" :label="false" max-length="50" :disabled="pending" />
                <span v-if="errorOf('email')" class="ds-dossier-field-error">{{ errorOf('email') }}</span>
            </div>
            <!-- СмартОтметка — в строке с e-mail, как в assets/employments. -->
            <div ref="smartSection" class="ds-dossier-switch-cell">
                <BooleanSwitch v-model="draft.smartCheckin" :disabled="!smartEditable || pending" :title="smartHint">СмартОтметка</BooleanSwitch>
                <span v-if="errorOf('smartCheckin')" class="ds-dossier-field-error">{{ errorOf('smartCheckin') }}</span>
            </div>
        </div>

        <div ref="employmentSection" class="ds-dossier-section-head">
            <span class="ds-dossier-section-title">Трудоустройства · {{ sortedEmployments.length }}</span>
        </div>
        <!-- Карточки ТУ: доступное открывает свой подэкран; недоступное — приглушено, с замком. -->
        <template v-for="item in sortedEmployments" :key="item.id">
            <button
                v-if="canOpen(item)"
                type="button"
                class="ds-dossier-employment"
                :disabled="pending"
                @click="emit('open-employment', item.id)"
            >
                <EmploymentCardText :item="item" :is-main="item.id === mainId" :from-one-c="fromOneC(item)" :today-key="todayKey" :schedule-name="shiftScheduleName(item)" />
                <ChevronRightIcon />
            </button>
            <div v-else class="ds-dossier-employment is-locked" :title="lockReason()">
                <EmploymentCardText :item="item" :is-main="item.id === mainId" :from-one-c="fromOneC(item)" :today-key="todayKey" :schedule-name="shiftScheduleName(item)" />
                <LockIcon />
            </div>
        </template>
        <button v-if="canCreateEmployment" type="button" class="ds-dossier-add-employment" :disabled="pending" @click="emit('new-employment')">
            <PlusIcon />Добавить трудоустройство
        </button>
    </div>

    <div class="ds-dossier-footer">
        <span v-if="message" class="ds-dossier-footer-message ds-dossier-field-error" role="alert">{{ message }}</span>
        <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="pending" @click="emit('back')">Отмена</button>
        <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!canSave" @click="save">{{ pending ? 'Сохранение…' : 'Сохранить' }}</button>
    </div>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, nextTick, onMounted, reactive, ref, watch, type PropType } from 'vue'
import BooleanSwitch from '@/ui/BooleanSwitch.vue'
import { editUser } from '@/api/users'
import { useDepartmentStore } from '@/stores/departmentStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import {
    type DossierEmployment,
    type DossierPerson,
    type EditFocus,
    type UserDraft,
    DASH,
    LOCKS_UNKNOWN_TITLE,
    ONE_C_LOCK_TITLE,
    buildUserPatch,
    describeWriteError,
    fieldErrorsOf,
    firedDayKey,
    formatDate,
    formatPercentage,
    isApiLocked,
    omitLocked,
    sortEmploymentsForList,
    userDraftFrom,
    validateUserDraft,
} from './dossierModel'
import { BackIcon, ChevronRightIcon, LockIcon, PlusIcon } from './dossierIcons'

// «Личные данные» (вариант B r2): «Сохранить» — только changed-only плоский PATCH User.
// ТУ здесь не сохраняются: каждое открывается своим подэкраном. Компонент остаётся смонтированным,
// пока открыт подэкран ТУ, — draft личных полей не теряется и не перетирается перечитыванием User.
const props = defineProps<{
    person: DossierPerson
    employments: DossierEmployment[]
    mainId: string | null
    todayKey: string
    focus: EditFocus
    canEditUser: boolean
    canChangeSmart: boolean
    canEditEmployment: boolean
    canCreateEmployment: boolean
    backLabel: string
    // Подтверждённая запись: инвалидация и перечитывание User; true — GET успешен.
    afterWrite: () => Promise<boolean>
    // Перечитывание User без записи (актуализация public_api_fields после серверного отказа).
    reloadUser: () => Promise<boolean>
}>()

const emit = defineEmits<{ back: [], saved: [refreshed: boolean], 'open-employment': [id: string], 'new-employment': [] }>()

const NO_RIGHTS = 'Недоступно с текущими правами'
const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()

const base = ref<DossierPerson>({ ...props.person })
const draft = reactive<UserDraft>(userDraftFrom(props.person))

type NameField = 'lastName' | 'firstName' | 'middleName'
const nameFields: { key: NameField, label: string }[] = [
    { key: 'lastName', label: 'Фамилия' },
    { key: 'firstName', label: 'Имя' },
    { key: 'middleName', label: 'Отчество' },
]
// Имена полей User в public_api_fields и в 400 body.
const USER_API_FIELDS: Record<keyof UserDraft, string> = {
    lastName: 'last_name',
    firstName: 'first_name',
    middleName: 'middle_name',
    phone: 'phone',
    email: 'email',
    smartCheckin: 'smart_checkin_possible',
}
// Актуальные locks из перечитанного User (не снимок base): draft при перечитывании сохраняется.
const userLocks = computed(() => props.person.publicApiFields)
const locksKnown = computed(() => userLocks.value !== null)
const oneCLockedCount = computed(() => (['lastName', 'firstName', 'middleName', 'phone', 'email'] as const)
    .filter(key => userLocks.value?.includes(USER_API_FIELDS[key])).length)
// Без права — все личные поля; неизвестный массив — запись User недоступна; иначе — только поля массива.
const fieldLocked = (key: keyof UserDraft) => !props.canEditUser || isApiLocked(userLocks.value, USER_API_FIELDS[key])
const lockTitle = (key: keyof UserDraft) => {
    if (!props.canEditUser) return NO_RIGHTS
    return locksKnown.value ? (fieldLocked(key) ? ONE_C_LOCK_TITLE : '') : LOCKS_UNKNOWN_TITLE
}

// СмартОтметка: UI can_change_smart_checkin_possible; запись — PATCH User (server can_edit_user_main_info).
const smartEditable = computed(() => props.canChangeSmart && props.canEditUser && base.value.smartCheckin !== null && locksKnown.value)
const smartHint = computed(() => {
    if (!props.canChangeSmart) return NO_RIGHTS
    if (!props.canEditUser) return 'Изменение недоступно без права на личные данные'
    if (!locksKnown.value) return LOCKS_UNKNOWN_TITLE
    return 'Отметка с телефона без QR'
})

// Закреплённые поля не входят в changed-only payload; неизвестный массив — пустой payload.
const patch = computed(() => omitLocked(buildUserPatch(base.value, draft, { main: props.canEditUser, smart: smartEditable.value }), userLocks.value))
const dirty = computed(() => Object.keys(patch.value).length > 0)
// Валидируются только редактируемые поля: значение из 1С не блокирует сохранение остальных.
const errors = computed(() => {
    const result = validateUserDraft(draft)
    for (const key of Object.keys(result) as (keyof UserDraft)[]) {
        if (fieldLocked(key)) delete result[key]
    }
    return result
})
const valid = computed(() => !dirty.value || Object.keys(errors.value).length === 0)
// Серверные 400 по полям (в т. ч. отказ закреплённого поля); сбрасываются при правке draft.
const serverErrors = ref<Partial<Record<keyof UserDraft, string>>>({})
watch(draft, () => { serverErrors.value = {} }, { deep: true })
const errorOf = (field: keyof UserDraft) => serverErrors.value[field]
    ?? (props.canEditUser && dirty.value ? errors.value[field] : undefined)

const pending = ref(false)
const message = ref('')
const canSave = computed(() => !pending.value && dirty.value && valid.value)

async function save() {
    if (!canSave.value) return
    pending.value = true
    message.value = ''
    try {
        await editUser({ ...patch.value }, base.value.id, { suppressGlobalErrorToast: true })
    } catch (error) {
        // Неопределённый результат не повторяется автоматически. 400 — у поля, draft сохраняется,
        // locks перечитываются (поле могло быть закреплено интеграцией после открытия формы).
        message.value = describeWriteError(error, 'Личные данные не сохранены')
        const byField = fieldErrorsOf(error)
        const mapped: Partial<Record<keyof UserDraft, string>> = {}
        for (const key of Object.keys(USER_API_FIELDS) as (keyof UserDraft)[]) {
            if (byField[USER_API_FIELDS[key]]) mapped[key] = byField[USER_API_FIELDS[key]]
        }
        pending.value = false
        if (Object.keys(byField).length) {
            await props.reloadUser()
            serverErrors.value = mapped
        }
        return
    }
    const refreshed = await props.afterWrite()
    pending.value = false
    emit('saved', refreshed)
}

// ---------- Список ТУ ----------
const sortedEmployments = computed(() => sortEmploymentsForList(props.employments, props.mainId, props.todayKey))
const inMyDepartments = (item: DossierEmployment) => Boolean(item.departmentId && departmentStore.myDepartments.some(dep => dep.id === item.departmentId))
const canOpen = (item: DossierEmployment) => props.canEditEmployment && inMyDepartments(item)
const lockReason = () => (!props.canEditEmployment ? 'Нет права редактировать трудоустройство' : 'Нет доступа к подразделению')
// «из 1С» — у ТУ есть поля, закреплённые интеграцией (Employment.public_api_fields, не User).
const fromOneC = (item: DossierEmployment) => Boolean(item.publicApiFields?.length)
const shiftScheduleName = (item: DossierEmployment) => (
    scheduleStore.shiftSchedules.find((option: { id: string }) => option.id === item.shiftScheduleId)?.name ?? null
)

// Две строки карточки: подразделение + бейджи; должность · доля · график · дата начала/завершения.
const EmploymentCardText = defineComponent({
    props: {
        item: { type: Object as PropType<DossierEmployment>, required: true },
        isMain: { type: Boolean, required: true },
        fromOneC: { type: Boolean, required: true },
        todayKey: { type: String, required: true },
        scheduleName: { type: String as PropType<string | null>, default: null },
    },
    setup(cardProps) {
        return () => {
            const { item } = cardProps
            const fired = firedDayKey(item)
            const ending = fired
                ? (fired >= cardProps.todayKey ? `последний рабочий день ${formatDate(item.firedAt)}` : `уволен с ${formatDate(item.firedAt)}`)
                : (item.hiredAt ? `с ${formatDate(item.hiredAt)}` : null)
            const details = [
                item.positionName || DASH,
                item.percentage === null ? null : formatPercentage(item.percentage),
                cardProps.scheduleName,
                ending,
            ].filter(Boolean).join(' · ')
            const badges = []
            if (cardProps.isMain) badges.push(h('span', { class: 'ds-dossier-badge is-main' }, 'основное'))
            if (cardProps.fromOneC) badges.push(h('span', { class: 'ds-dossier-badge', title: 'Часть полей заполняется из 1С' }, [h(LockIcon), 'из 1С']))
            return h('span', { class: 'ds-dossier-employment-text' }, [
                h('span', { class: 'ds-dossier-employment-title' }, [item.departmentName || DASH, ...badges]),
                h('span', { class: 'ds-dossier-employment-details' }, details),
            ])
        }
    },
})

// ---------- Переход к блоку из меню ----------
const userSection = ref<HTMLElement | null>(null)
const smartSection = ref<HTMLElement | null>(null)
const employmentSection = ref<HTMLElement | null>(null)
const scrollToFocus = async () => {
    await nextTick()
    const target = props.focus === 'employment' ? employmentSection.value : props.focus === 'smart' ? smartSection.value : userSection.value
    target?.scrollIntoView({ block: 'start' })
}
onMounted(scrollToFocus)
watch(() => props.focus, scrollToFocus)
</script>
